import React, { useState } from 'react';
import { TabsRoot, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useCategory } from '@/contexts/CategoryManagementContext';
import { getCategoryForItem, formatDateBR, sortByColumn } from '@/lib/utils';
import { Edit2, Trash2, ArrowUpDown, ArrowUp, ArrowDown, History, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import EditableField from '@/components/shared/EditableField';
import { useAuditLog } from '@/contexts/AuditLogContext';

const ListsView = ({ 
  goals, 
  services, 
  attendance, 
  implementations,
  onUpdate,
  onDelete 
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'dueDate', direction: 'asc' });
  const [filterText, setFilterText] = useState('');
  const { categories } = useCategory();
  const { logs } = useAuditLog();

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    let filtered = data.filter(item => 
      (item.name || item.title || '').toLowerCase().includes(filterText.toLowerCase())
    );
    return sortByColumn(filtered, sortConfig.key, sortConfig.direction);
  };

  const DataTable = ({ data, type, nameField = 'name' }) => {
    const sortedData = getSortedData(data);

    if (sortedData.length === 0) {
      return (
        <div className="p-12 text-center text-gray-400 border border-dashed border-gray-700 rounded-xl bg-white/5">
          Nenhum item encontrado nesta lista.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto rounded-xl border border-gray-800 bg-black/40 shadow-xl">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-900/50 text-gray-400 uppercase text-xs font-bold tracking-wider">
            <tr>
              <th className="px-6 py-4 cursor-pointer hover:text-white transition-colors" onClick={() => handleSort(nameField)}>
                <div className="flex items-center gap-2">
                  Nome
                  {sortConfig.key === nameField && (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                </div>
              </th>
              <th className="px-6 py-4 cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('openingDate')}>
                <div className="flex items-center gap-2">Abertura</div>
              </th>
              <th className="px-6 py-4 cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('dueDate')}>
                <div className="flex items-center gap-2">
                  Prazo
                   {sortConfig.key === 'dueDate' && (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                </div>
              </th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Responsável/Cliente</th>
              <th className="px-6 py-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {sortedData.map((item) => {
              const category = getCategoryForItem(item, categories);
              const rowStyle = category ? { borderLeft: `4px solid ${category.color}` } : {};
              
              return (
                <tr 
                  key={item.id} 
                  className="hover:bg-white/5 transition-colors group"
                  style={rowStyle}
                >
                  <td className="px-6 py-4 font-medium text-white">
                    <EditableField 
                      value={item[nameField]} 
                      onSave={(f, v, oldVal) => onUpdate(type, item.id, nameField, v, item[nameField], oldVal)}
                      entityId={`${type}-${item.id}`}
                      fieldName={nameField}
                      className="text-white"
                    />
                  </td>
                  <td className="px-6 py-4 text-gray-400">
                    {formatDateBR(item.openingDate)}
                  </td>
                  <td className="px-6 py-4 text-gray-300 font-mono">
                    <EditableField 
                      value={item.dueDate} 
                      onSave={(f, v, oldVal) => onUpdate(type, item.id, 'dueDate', v, item[nameField], oldVal)}
                      entityId={`${type}-${item.id}`}
                      fieldName="dueDate"
                      type="date"
                      className="text-gray-300"
                    />
                    {category && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide" style={{ color: category.color, backgroundColor: `${category.color}20` }}>
                        {category.name}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                     <EditableField 
                      value={item.lifecycleStatus} 
                      onSave={(f, v, oldVal) => onUpdate(type, item.id, 'lifecycleStatus', v, item[nameField], oldVal)}
                      entityId={`${type}-${item.id}`}
                      fieldName="lifecycleStatus"
                      type="select"
                      options={[{value:'Aberto', label:'Aberto'}, {value:'Concluído', label:'Concluído'}]}
                      className={item.lifecycleStatus === 'Concluído' ? 'text-green-400' : 'text-blue-400'}
                    />
                  </td>
                  <td className="px-6 py-4 text-gray-400">
                    <EditableField 
                      value={item.assignedTo || item.client || '-'} 
                      onSave={(f, v, oldVal) => {
                        const fieldName = item.assignedTo !== undefined ? 'assignedTo' : 'client';
                        onUpdate(type, item.id, fieldName, v, item[nameField], oldVal);
                      }}
                      entityId={`${type}-${item.id}`}
                      fieldName={item.assignedTo !== undefined ? 'assignedTo' : 'client'}
                      className="text-gray-400"
                    />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-red-500/20 hover:text-red-400 text-gray-500" onClick={() => onDelete(item, type)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center bg-white/5 p-4 rounded-xl border border-white/10">
        <h2 className="text-xl font-bold text-white">Listagem Completa</h2>
        <input 
          type="text" 
          placeholder="Filtrar por nome..." 
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="bg-black/50 border border-gray-700 rounded-lg px-4 py-2 text-sm text-white focus:outline-none focus:border-orange-500 w-64"
        />
      </div>

      <TabsRoot defaultValue="goals" className="w-full">
        <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl w-full sm:w-auto flex flex-wrap h-auto gap-2">
          <TabsTrigger value="goals" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-gray-400 flex-1 sm:flex-none">Metas</TabsTrigger>
          <TabsTrigger value="services" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-gray-400 flex-1 sm:flex-none">Serviços</TabsTrigger>
          <TabsTrigger value="attendance" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-gray-400 flex-1 sm:flex-none">Atendimentos</TabsTrigger>
          <TabsTrigger value="implementations" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-gray-400 flex-1 sm:flex-none">Implementações</TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-gray-400 flex-1 sm:flex-none">
            <History className="w-4 h-4 mr-1" />
            Histórico
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="goals"><DataTable data={goals} type="goals" /></TabsContent>
          <TabsContent value="services"><DataTable data={services} type="services" /></TabsContent>
          <TabsContent value="attendance"><DataTable data={attendance} type="attendance" nameField="title" /></TabsContent>
          <TabsContent value="implementations"><DataTable data={implementations} type="implementations" /></TabsContent>
          <TabsContent value="history">
            <div className="overflow-x-auto rounded-xl border border-gray-800 bg-black/40 shadow-xl">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-900/50 text-gray-400 uppercase text-xs font-bold tracking-wider">
                  <tr>
                    <th className="px-6 py-4">Data/Hora</th>
                    <th className="px-6 py-4">Usuário</th>
                    <th className="px-6 py-4">Campo</th>
                    <th className="px-6 py-4">Valor Anterior</th>
                    <th className="px-6 py-4">Novo Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                        Nenhum histórico de alterações encontrado.
                      </td>
                    </tr>
                  ) : (
                    logs
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((log) => (
                        <tr key={log.id} className="hover:bg-white/5 transition-colors">
                          <td className="px-6 py-4 text-gray-400">
                            {new Date(log.timestamp).toLocaleString('pt-BR')}
                          </td>
                          <td className="px-6 py-4 text-white font-medium">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              {log.userName}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-300">
                            {log.fieldName}
                          </td>
                          <td className="px-6 py-4 text-red-400 font-mono text-sm">
                            {log.oldValue || '-'}
                          </td>
                          <td className="px-6 py-4 text-green-400 font-mono text-sm">
                            {log.newValue || '-'}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </div>
      </TabsRoot>
    </div>
  );
};

export default ListsView;